import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { PROFILE_QUERY_KEY } from '@/hooks/useProfile';
import { log } from '@/lib/logger';
import { typedSelect, typedUpdate } from '@/lib/supabaseTypedQuery';
import { timeZoneToBackfill } from '@/lib/userTimeZone';

export type DateFormatOption = 'dd/MM/yy' | 'MM/dd/yy' | 'yyyy-MM-dd' | 'dd MMM yyyy';

export interface UserSettings {
  dateFormat: DateFormatOption;
  timezone: string;
  tooltipsEnabled: boolean;
}

const DEFAULT_SETTINGS: UserSettings = {
  dateFormat: 'dd/MM/yy',
  // profiles.timezone is the user-calendar contract. Until it is loaded (or
  // when it is missing), use the same deterministic fallback as Edge code.
  timezone: 'UTC',
  tooltipsEnabled: true };

interface UserSettingsRow {
  date_format: string | null;
  timezone: string | null;
  tooltips_enabled: boolean | null;
}

/**
 * The signed-in user's settings, keyed by user id. Signing in, signing out or
 * switching user changes the key, so the new user's row is read; a token
 * refresh keeps the same id and reads nothing.
 */
const USER_SETTINGS_KEY_ROOT = ['user-settings'] as const;
export const userSettingsQueryKey = (userId: string | null) => [...USER_SETTINGS_KEY_ROOT, userId] as const;

async function readUserSettings(userId: string): Promise<UserSettings> {
  const { data, error } = await typedSelect<UserSettingsRow>(
    'profiles',
    'date_format, timezone, tooltips_enabled'
  )
    .eq('id', userId)
    .single();

  if (error) throw error;
  if (!data) return DEFAULT_SETTINGS;

  // Server text (Assist, MCP, briefings) reads profiles.timezone, so a
  // never-chosen zone is saved from the browser on the next login.
  let storedZone = data.timezone;
  const learnedZone = timeZoneToBackfill(storedZone);
  if (learnedZone) {
    const { error: zoneError } = await typedUpdate<UserSettingsRow>('profiles', { timezone: learnedZone })
      .eq('id', userId);
    if (zoneError) log.warn('Saving browser time zone to profile failed', { error: zoneError.message });
    else storedZone = learnedZone;
  }

  return {
    dateFormat: (data.date_format as DateFormatOption) || DEFAULT_SETTINGS.dateFormat,
    timezone: storedZone || DEFAULT_SETTINGS.timezone,
    tooltipsEnabled: data.tooltips_enabled ?? true };
}

export const useUserSettings = () => {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: userSettingsQueryKey(userId),
    enabled: !!userId,
    queryFn: () => {
      if (!userId) throw new Error('Not signed in');
      return readUserSettings(userId);
    },
    // Settings change only through this hook or the profile screen, and both
    // refresh this key, so nothing re-reads them in the background.
    staleTime: Infinity,
    // The app keeps the previous key's data while a new key loads. Here that
    // would show the last user's settings to the next one, so turn it off.
    placeholderData: undefined,
    // A failed read is reported once, as an error, by the app's query cache
    // after the last retry. The hook itself logs nothing, so no retry doubles it.
    meta: { showErrorToast: false },
  });

  // Only the signed-in user's settings stay cached. Someone who signs back in
  // later in this tab gets a fresh read, not settings kept from before the switch.
  // A token refresh keeps the same id, so this does not run and nothing is re-read.
  useEffect(() => {
    queryClient.removeQueries({
      queryKey: USER_SETTINGS_KEY_ROOT,
      predicate: cached => cached.queryKey[1] !== userId,
    });
  }, [queryClient, userId]);

  // A failed read falls back to the defaults rather than blocking the app.
  const settings = (userId ? query.data : undefined) ?? DEFAULT_SETTINGS;
  // Loading until auth has settled and, when signed in, this user's row is read.
  const loading = authLoading || (!!userId && query.isPending);

  const saveSetting = useCallback(async (
    column: Partial<UserSettingsRow>,
    patch: Partial<UserSettings>,
    messages: { success: string; failure: string; log: string },
  ) => {
    if (!userId) {
      toast.error('Not authenticated');
      return;
    }

    try {
      const { error } = await typedUpdate<UserSettingsRow>('profiles', column).eq('id', userId);
      if (error) throw error;

      queryClient.setQueryData<UserSettings>(userSettingsQueryKey(userId), prev => ({ ...(prev ?? DEFAULT_SETTINGS), ...patch }));
      // The profile query reads date_format and timezone too. It does not read
      // tooltips_enabled, and refreshing it resets the unsaved profile form.
      if (patch.dateFormat !== undefined || patch.timezone !== undefined) {
        void queryClient.invalidateQueries({ queryKey: [PROFILE_QUERY_KEY, userId] });
      }
      toast.success(messages.success);
    } catch (error) {
      log.error(messages.log, error);
      toast.error(messages.failure);
    }
  }, [queryClient, userId]);

  const updateDateFormat = useCallback((dateFormat: DateFormatOption) => saveSetting(
    { date_format: dateFormat },
    { dateFormat },
    { success: 'Date format updated', failure: 'Failed to update date format', log: 'Error updating date format:' },
  ), [saveSetting]);

  const updateTimezone = useCallback((timezone: string) => saveSetting(
    { timezone },
    { timezone },
    { success: 'Timezone updated', failure: 'Failed to update timezone', log: 'Error updating timezone:' },
  ), [saveSetting]);

  const updateTooltipsEnabled = useCallback((tooltipsEnabled: boolean) => saveSetting(
    { tooltips_enabled: tooltipsEnabled },
    { tooltipsEnabled },
    {
      success: tooltipsEnabled ? 'Help tips on' : 'Help tips off',
      failure: 'Failed to update help tips',
      log: 'Error updating tooltip preference:',
    },
  ), [saveSetting]);

  return {
    settings,
    loading,
    updateDateFormat,
    updateTimezone,
    updateTooltipsEnabled,
    refetch: query.refetch };
};
